import { useEffect } from "react";

export default function Privacy(){
  useEffect(()=>{ document.title = "Privacy Policy | MikDok"; },[]);
  return (
    <main style={{
      maxWidth:900,
      margin:"48px auto",
      padding:"0 22px",
      lineHeight:1.7,
      color:"#0f172a",
      fontFamily:"system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif"
    }}>
      <h1 style={{fontSize:"2rem",marginBottom:8,fontWeight:800}}>Privacy Policy</h1>
      <p style={{color:"#64748b"}}>
        <strong>Effective date:</strong> 13 October 2025
      </p>

      <p>
        This Privacy Policy explains how <strong>MikDok</strong> (“<strong>we</strong>”, “<strong>us</strong>”, “<strong>our</strong>”)
        collects, uses, stores, and protects your personal data when you use
        <a href="https://mikdok.com" target="_blank" rel="noreferrer"> mikdok.com</a>
        and its related digital services (collectively, the “<strong>Platform</strong>”).
      </p>

      <p>
        MikDok is currently operated by its founders  —
        pending formal company registration in the Republic of Albania.  
        We follow the <strong>EU General Data Protection Regulation (GDPR)</strong> and applicable Albanian data-protection laws.
      </p>

      <h2>1. Data Controller</h2>
      <p>
        Until incorporation, the MikDok Founders act jointly as Data Controllers responsible for your data.  
        Contact: <a href="mailto:contact@mikdok.com">contact@mikdok.com</a>.
      </p>

      <h2>2. Personal Data We Collect</h2>
      <ul>
        <li><strong>Account information:</strong> name, email, password (hash only), role (patient, doctor, clinic, admin).</li>
        <li><strong>Professional data (for doctors/clinics):</strong> education, specialties, license ID, clinic address, phone, working hours, pricing, profile photo, biography.</li>
        <li><strong>Patient data:</strong> basic contact info, age range, appointment requests, messages, uploaded documents (e.g., x-rays or reports).</li>
        <li><strong>Device and usage data:</strong> IP address (truncated/anonymized), browser type, device ID, session duration, pages visited (analytics via Google Analytics 4).</li>
        <li><strong>Location data:</strong> approximate geolocation if you allow it in the map interface to locate nearby doctors/clinics.</li>
        <li><strong>Cookies and local storage:</strong> for login sessions, preferences, analytics (see <a href="/cookies">Cookie Policy</a>).</li>
        <li><strong>Communications:</strong> chat messages between patients and doctors, support tickets, or emails.</li>
      </ul>

      <h2>3. How We Use Your Data</h2>
      <ul>
        <li>To create and manage your account.</li>
        <li>To display doctor/clinic profiles, reviews, and listings to users.</li>
        <li>To facilitate communication and appointment requests.</li>
        <li>To personalize search results and recommendations.</li>
        <li>To send transactional notifications (bookings, verification, updates).</li>
        <li>To improve security, detect abuse, and maintain the Platform.</li>
        <li>To perform anonymized analytics for performance and UX improvement.</li>
        <li>For future Telehealth or payment features, only after additional consent.</li>
      </ul>

      <h2>4. Legal Bases for Processing</h2>
      <ul>
        <li><strong>Contractual necessity</strong> – to provide Platform services (account, bookings, messages).</li>
        <li><strong>Legitimate interests</strong> – to ensure security, prevent fraud, and enhance user experience.</li>
        <li><strong>Consent</strong> – for analytics, marketing, cookies, and optional communications.</li>
        <li><strong>Legal obligation</strong> – to comply with health or tax laws if required.</li>
      </ul>

      <h2>5. Data Storage & Security</h2>
      <p>
        MikDok uses <strong>Firebase and Google Cloud Platform</strong> (EU region datacenters) for hosting, database, authentication, and file storage.
        Data is encrypted at rest (AES-256) and in transit (TLS 1.3).
      </p>
      <ul>
        <li>Access to patient data is restricted to authorized roles only.</li>
        <li>Passwords are stored as secure hashes using industry-standard algorithms.</li>
        <li>Audit logs track sensitive operations (login, upload, deletion).</li>
        <li>We maintain daily encrypted backups for disaster recovery.</li>
      </ul>

      <h2>6. Data Sharing</h2>
      <p>We do <strong>not</strong> sell or rent personal data. We share it only when necessary:</p>
      <ul>
        <li>With <strong>doctors and clinics</strong> you contact or book through MikDok.</li>
        <li>With <strong>Firebase (Google LLC)</strong> for hosting, messaging, authentication, analytics.</li>
        <li>With <strong>Google Maps API</strong> for location visualization (subject to your consent).</li>
        <li>With <strong>email providers (Brevo or Gmail API)</strong> for sending transactional messages.</li>
        <li>With payment processors (future feature) only to execute transactions securely.</li>
        <li>With public authorities if required by law or court order.</li>
      </ul>

      <h2>7. International Data Transfers</h2>
      <p>
        Google LLC and affiliated Firebase entities are certified under the EU-US Data Privacy Framework and use Standard Contractual Clauses (SCCs) to ensure adequate protection for data transfers outside the EEA.
      </p>

      <h2>8. Data Retention</h2>
      <ul>
        <li>Account data – kept while the account is active and deleted within 90 days after closure (unless legally required).</li>
        <li>Logs – retained for up to 12 months for security purposes.</li>
        <li>Messages – kept for as long as your account exists or until you delete them.</li>
        <li>Analytics data – aggregated and anonymized after 24 months.</li>
      </ul>

      <h2>9. Your Rights (GDPR)</h2>
      <ul>
        <li><strong>Access</strong> – request a copy of your personal data we hold.</li>
        <li><strong>Rectification</strong> – correct inaccurate or incomplete data.</li>
        <li><strong>Erasure</strong> (“right to be forgotten”) – delete your account and associated data.</li>
        <li><strong>Restriction</strong> – limit processing under certain conditions.</li>
        <li><strong>Data portability</strong> – receive your data in a machine-readable format.</li>
        <li><strong>Objection</strong> – oppose processing based on legitimate interest or direct marketing.</li>
        <li><strong>Withdraw consent</strong> – at any time for analytics or marketing cookies.</li>
      </ul>
      <p>
        To exercise rights, contact us at <a href="mailto:contact@mikdok.com">contact@mikdok.com</a>.  
        You may also file a complaint with the Albanian Data Protection Commissioner (“Komisioneri për Mbrojtjen e Të Dhenave Personale”).
      </p>

      <h2>10. Cookies & Tracking</h2>
      <p>
        We use cookies and local storage for authentication, functionality, analytics, and marketing (subject to consent).  
        See our <a href="/cookies">Cookie Policy</a> for details and control options.
      </p>

      <h2>11. Security Measures</h2>
      <ul>
        <li>End-to-end TLS encryption for all traffic.</li>
        <li>Two-factor authentication for admin accounts.</li>
        <li>Automatic logout after inactivity.</li>
        <li>Access controls and least-privilege permissions for staff and developers.</li>
        <li>Regular vulnerability scans and third-party library updates.</li>
      </ul>

      <h2>12. Children’s Privacy</h2>
      <p>
        MikDok is not intended for children under 16. We do not knowingly collect personal data from minors.  
        If you believe a child has provided data without parental consent, contact us for removal.
      </p>

      <h2>13. Future Features (Telehealth & Payments)</h2>
      <p>
        If MikDok introduces video consultations or payments, additional privacy controls and agreements will be implemented before launch, including:
      </p>
      <ul>
        <li>End-to-end encrypted video sessions.</li>
        <li>Explicit patient consent for telehealth data processing.</li>
        <li>Payment data handled exclusively by licensed processors (PayPal, Stripe, banks).</li>
        <li>No storage of payment card details on MikDok servers.</li>
      </ul>

      <h2>14. Automated Decisions & Profiling</h2>
      <p>
        MikDok may use algorithmic ranking to sort search results (e.g., by location, reviews, activity).  
        No automated decision produces legal effects without human review.
      </p>

      <h2>15. Data Breach Notification</h2>
      <p>
        In the event of a data breach that poses a risk to your rights or freedoms, we will notify you and the supervisory authority within 72 hours, as required by GDPR Article 33.
      </p>

      <h2>16. Third-Party Links</h2>
      <p>
        Our Platform may contain links to external sites (e.g., clinic websites, social media).  
        We are not responsible for their privacy practices and encourage you to review their policies independently.
      </p>

      <h2>17. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy periodically. The latest version will always be available at 
        <a href="https://mikdok.com/privacy">mikdok.com/privacy</a> with an updated effective date.  
        For significant changes, we’ll notify users by email or in-app banner.
      </p>

      <h2>18. Contact</h2>
      <p>
        For privacy questions or data requests, email us at <a href="mailto:contact@mikdok.com">contact@mikdok.com</a>.<br/>
        Jurisdiction: Republic of Albania (EU GDPR Zone)
      </p>

      <p style={{color:"#64748b",marginTop:24}}>
        © {new Date().getFullYear()} MikDok — All rights reserved.  
        This Privacy Policy forms part of the MikDok Terms and Conditions.
      </p>
    </main>
  );
}
